import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { profileApi } from '../../services/api';
import { Certification, Education, Experience, Profile, ProfilePatch, Skill } from '../../types';
import { errorMessage } from '../../utils/errors';
import { useToast } from '../../hooks/useToast';
import { useAuthStore } from '../../store/authStore';
import ProfileDetailsForm from '../../components/profile/ProfileDetailsForm';
import EducationSection from '../../components/profile/EducationSection';
import ExperienceSection from '../../components/profile/ExperienceSection';
import SkillsSection from '../../components/profile/SkillsSection';
import CertificationsSection from '../../components/profile/CertificationsSection';
import DeleteAccount from '../../components/profile/DeleteAccount';

const TABS = ['Details', 'Education', 'Experience', 'Skills', 'Certifications'] as const;

const ProfilePage: React.FC = () => {
  const { user, updateUser } = useAuthStore();
  const { show, toast } = useToast();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [profile, setProfile] = useState<Profile | null>(null);
  const [education, setEducation] = useState<Education[]>([]);
  const [experience, setExperience] = useState<Experience[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [certifications, setCertifications] = useState<Certification[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, edu, exp, sk, certs] = await Promise.all([
        profileApi.get(),
        profileApi.education.list(),
        profileApi.experience.list(),
        profileApi.skills.list(),
        profileApi.certifications.list(),
      ]);
      setProfile(p);
      setEducation(edu);
      setExperience(exp);
      setSkills(sk);
      setCertifications(certs);
    } catch (e) {
      setError(errorMessage(e, 'Could not load your profile.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaveDetails = async (patch: ProfilePatch) => {
    setSaving(true);
    try {
      const updated = await profileApi.patch(patch);
      setProfile(updated);
      // Keep the header/avatar in sync when names change.
      if (user && (patch.first_name !== undefined || patch.last_name !== undefined)) {
        updateUser({
          ...user,
          first_name: updated.first_name,
          last_name: updated.last_name,
        });
      }
      show('Profile saved.');
    } catch (e) {
      show(errorMessage(e, 'Could not save your profile.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  /** Wrap a list mutation so failures surface as a snackbar and rethrow. */
  const guarded = async (fn: () => Promise<void>, successMessage: string) => {
    try {
      await fn();
      show(successMessage);
    } catch (e) {
      show(errorMessage(e, 'Something went wrong.'), 'error');
      throw e;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          Profile
        </Typography>
        <Typography variant="body1" color="text.secondary">
          The extension fills applications from this profile. Keep it accurate and complete.
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : profile ? (
        <Card variant="outlined">
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            {TABS.map((label) => (
              <Tab key={label} label={label} />
            ))}
          </Tabs>
          <CardContent sx={{ p: 3 }}>
            {tab === 0 && (
              <ProfileDetailsForm profile={profile} saving={saving} onSave={handleSaveDetails} />
            )}
            {tab === 1 && (
              <EducationSection
                items={education}
                onCreate={(data) =>
                  guarded(async () => {
                    const created = await profileApi.education.create(data);
                    setEducation((prev) => [...prev, created]);
                  }, 'Education added.')
                }
                onUpdate={(id, data) =>
                  guarded(async () => {
                    const updated = await profileApi.education.update(id, data);
                    setEducation((prev) => prev.map((e) => (e.id === id ? updated : e)));
                  }, 'Education updated.')
                }
                onDelete={(id) =>
                  guarded(async () => {
                    await profileApi.education.remove(id);
                    setEducation((prev) => prev.filter((e) => e.id !== id));
                  }, 'Education removed.')
                }
              />
            )}
            {tab === 2 && (
              <ExperienceSection
                items={experience}
                onCreate={(data) =>
                  guarded(async () => {
                    const created = await profileApi.experience.create(data);
                    setExperience((prev) => [...prev, created]);
                  }, 'Experience added.')
                }
                onUpdate={(id, data) =>
                  guarded(async () => {
                    const updated = await profileApi.experience.update(id, data);
                    setExperience((prev) => prev.map((e) => (e.id === id ? updated : e)));
                  }, 'Experience updated.')
                }
                onDelete={(id) =>
                  guarded(async () => {
                    await profileApi.experience.remove(id);
                    setExperience((prev) => prev.filter((e) => e.id !== id));
                  }, 'Experience removed.')
                }
              />
            )}
            {tab === 3 && (
              <SkillsSection
                items={skills}
                onCreate={(data) =>
                  guarded(async () => {
                    const created = await profileApi.skills.create(data);
                    setSkills((prev) => [...prev, created]);
                  }, 'Skill added.')
                }
                onUpdate={(id, data) =>
                  guarded(async () => {
                    const updated = await profileApi.skills.update(id, data);
                    setSkills((prev) => prev.map((s) => (s.id === id ? updated : s)));
                  }, 'Skill updated.')
                }
                onDelete={(id) =>
                  guarded(async () => {
                    await profileApi.skills.remove(id);
                    setSkills((prev) => prev.filter((s) => s.id !== id));
                  }, 'Skill removed.')
                }
              />
            )}
            {tab === 4 && (
              <CertificationsSection
                items={certifications}
                onCreate={(data) =>
                  guarded(async () => {
                    const created = await profileApi.certifications.create(data);
                    setCertifications((prev) => [...prev, created]);
                  }, 'Certification added.')
                }
                onUpdate={(id, data) =>
                  guarded(async () => {
                    const updated = await profileApi.certifications.update(id, data);
                    setCertifications((prev) => prev.map((c) => (c.id === id ? updated : c)));
                  }, 'Certification updated.')
                }
                onDelete={(id) =>
                  guarded(async () => {
                    await profileApi.certifications.remove(id);
                    setCertifications((prev) => prev.filter((c) => c.id !== id));
                  }, 'Certification removed.')
                }
              />
            )}
          </CardContent>
        </Card>
      ) : null}

      <DeleteAccount />

      {toast}
    </Container>
  );
};

export default ProfilePage;
